import React, { useEffect, useState } from "react";
import { Subject } from "rxjs";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { map, takeUntil, tap } from "rxjs/operators";
import withErrorGuard from "../../HOC/withErrorGuard";
import apiClient from "../../services/apiClient";
import Loader from "../../components/Loader";
import TimeSince from "../../components/social/TimeSince";
import SSOUser from "../../components/social/SSOUser";
import store from "../../state/store";
import CommentForm from "../../components/social/CommentForm";
import Comment from "../../models/Comment";
import AddToast from "../../state/actions/AddToast";
import { getCurrentUserSsoId } from "../../state/selectors/general.selectors";

function getLink(objectType, representation, id) {
  const host = store.getState().varsomeHost;
  const isVariant = objectType === "variant";
  const genome = String(id).slice(0, 4) === "1019" ? "hg19" : "hg38";
  const path = isVariant ? `${genome}/${representation}` : representation;
  return `${host}/${objectType}/${path}#community-contributions`;
}

function RecentComments({ ssoId, currentUserSsoId, canEdit }) {
  const availableTypes = new Set(["gene", "variant"]);
  const [editted, setEditted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [comments, setComments] = useState([]);

  const handleDelete = (obj) => {
    const { topic_slug: slug, id } = obj;
    const { type, typeId, subType, subTypeId } = Comment.extractSlugInfo(slug);
    apiClient.deleteComment(type, typeId, subType, subTypeId, id).subscribe({
      next: () => {
        setComments(comments.filter((x) => x !== obj));
        AddToast("Comment Deleted successfully", "success");
      },
      erorr: () => AddToast("Error while deleting comment"),
    });
  };

  const handleEdit = (obj, str) => {
    const { topic_slug: slug, id } = obj;
    const { type, typeId, subType, subTypeId } = Comment.extractSlugInfo(slug);

    return apiClient
      .editComment(type, typeId, subType, subTypeId, str, id)
      .pipe(map((x) => x?.result?.post))
      .subscribe({
        next: () => {
          const updated = comments.map((x) =>
            x === obj ? { ...x, comment: str } : x
          );
          setComments(updated);
          AddToast("Comment updated successfully.", "success");
        },
        error: () =>
          AddToast("Something went wrong while updating the comment."),
      });
  };

  useEffect(() => {
    const destroy$ = new Subject();
    setIsLoading(true);

    apiClient
      .getActivity(ssoId)
      .pipe(
        takeUntil(destroy$),
        map((x) => x.latest_posts),
        map((arr) => {
          return arr
            .map((x) => ({
              ...x,
              objectType: x.object_type,
              representation: x.representation,
              ssoId: x.user_sso_id_hashed,
              createdAt: new Date(x.created_at),
              link: getLink(x.object_type, x.representation, x.object_id),
            }))
            .sort((a, b) => b.createdAt - a.createdAt);
        }),
        tap(() => setIsLoading(false))
      )
      .subscribe({
        next: (activities) => setComments(activities),
        error: (err) => {
          setIsLoading(false);
          setComments([]);
          console.error(err);
        },
      });

    return () => destroy$.next();
  }, []);

  return (
    <div className="tw-pt-6">
      {isLoading ? (
        <Loader />
      ) : (
        comments
          .filter((x) => availableTypes.has(x.objectType))
          .map((x) => (
            <div
              key={x.id}
              className="tw-p-3 tw-border-b tw-border-gray-200 tw-flex tw-items-start"
            >
              <div className="tw-flex-grow">
                <i className="fa fa-comments-o tw-pr-2" />
                <SSOUser ssoId={x.ssoId} />
                <span className="tw-px-2">posted a comment on</span>
                <a href={x.link}>{x.representation}</a>
                <div>
                  <div className="tw-inline-block">
                    <TimeSince date={x.createdAt} />
                  </div>
                </div>

                {editted === x ? (
                  <CommentForm
                    value={x.excerpt}
                    onSubmit={(str) => handleEdit(x, str)}
                    onDelete={() => handleDelete(x)}
                    onCancel={() => setEditted(null)}
                  />
                ) : null}
              </div>

              {canEdit && currentUserSsoId === x.ssoId && !(editted === x) ? (
                <button
                  className="btn btn-success"
                  onClick={() => setEditted(x)}
                  type="submit"
                >
                  <i className="fa fa-comments-o tw-text-white" />
                  <span className="tw-px-1">Edit</span>
                </button>
              ) : null}
            </div>
          ))
      )}
    </div>
  );
}

RecentComments.propTypes = {
  ssoId: PropTypes.string,
  currentUserSsoId: PropTypes.string,
  canEdit: PropTypes.bool,
};

RecentComments.defaultProps = {
  ssoId: undefined,
  currentUserSsoId: undefined,
  canEdit: false,
};

const mapStateToProps = (state) => {
  const currentUserSsoId = getCurrentUserSsoId(state);
  return { currentUserSsoId };
};

export default connect(
  mapStateToProps,
  {}
)(
  withErrorGuard(
    React.memo(RecentComments),
    "RecentComments",
    "Recent Comments"
  )
);
